import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAdminNotifications, type AdminNotification, type NotificationSeverity } from "@/hooks/useAdminNotifications";

const SEVERITY_CONFIG: Record<NotificationSeverity, { icon: string; color: string; bg: string; badge: string }> = {
  error: {
    icon: "ri-error-warning-fill",
    color: "text-red-500",
    bg: "bg-red-50",
    badge: "bg-red-500",
  },
  warning: {
    icon: "ri-alert-fill",
    color: "text-amber-500",
    bg: "bg-amber-50",
    badge: "bg-amber-500",
  },
  info: {
    icon: "ri-information-fill",
    color: "text-sky-500",
    bg: "bg-sky-50",
    badge: "bg-sky-500",
  },
};

const TYPE_LABELS: Record<AdminNotification["type"], string> = {
  broken_link: "Link inválido",
  no_link: "Sem link",
  low_stock: "Estoque baixo",
  expired_deal: "Oferta expirada",
};

export default function NotificationBell() {
  const { notifications, errorCount, warningCount, totalCount } = useAdminNotifications();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationSeverity | "all">("all");
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const filtered = filter === "all" ? notifications : notifications.filter((n) => n.severity === filter);

  const bellColor =
    errorCount > 0
      ? "text-red-500"
      : warningCount > 0
      ? "text-amber-500"
      : totalCount > 0
      ? "text-sky-500"
      : "text-gray-400";

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer ${bellColor}`}
      >
        <i className="ri-notification-3-line text-xl"></i>
        {totalCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-bold px-1 ${
              errorCount > 0 ? "bg-red-500" : warningCount > 0 ? "bg-amber-500" : "bg-sky-500"
            }`}
          >
            {totalCount > 99 ? "99+" : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[420px] bg-white rounded-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Notificações</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {totalCount === 0
                  ? "Tudo certo! Nenhum alerta."
                  : `${totalCount} alerta${totalCount !== 1 ? "s" : ""} encontrado${totalCount !== 1 ? "s" : ""}`}
              </p>
            </div>
            {totalCount > 0 && (
              <div className="flex items-center gap-2">
                {errorCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                    <i className="ri-error-warning-fill text-xs"></i>
                    {errorCount} erro{errorCount !== 1 ? "s" : ""}
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                    <i className="ri-alert-fill text-xs"></i>
                    {warningCount}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Filter tabs */}
          {totalCount > 0 && (
            <div className="px-5 py-2.5 border-b border-gray-50 flex gap-1">
              {(["all", "error", "warning", "info"] as const).map((f) => {
                const count =
                  f === "all"
                    ? totalCount
                    : notifications.filter((n) => n.severity === f).length;
                if (f !== "all" && count === 0) return null;
                return (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      filter === f
                        ? f === "all"
                          ? "bg-gray-900 text-white"
                          : f === "error"
                          ? "bg-red-500 text-white"
                          : f === "warning"
                          ? "bg-amber-500 text-white"
                          : "bg-sky-500 text-white"
                        : "text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {f === "all" ? "Todos" : f === "error" ? "Erros" : f === "warning" ? "Avisos" : "Info"}
                    {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Notifications list */}
          <div className="max-h-[380px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-3">
                  <i className="ri-checkbox-circle-line text-green-500 text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700">Tudo certo!</p>
                <p className="text-xs text-gray-400 mt-1">Nenhum alerta nesta categoria.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {filtered.map((notif) => {
                  const cfg = SEVERITY_CONFIG[notif.severity];
                  return (
                    <Link
                      key={notif.id}
                      to={notif.link}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${cfg.bg}`}>
                        <i className={`${cfg.icon} text-base ${cfg.color}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-bold text-gray-800">{notif.title}</p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white ${cfg.badge}`}>
                            {TYPE_LABELS[notif.type]}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{notif.description}</p>
                        <p className="text-xs text-orange-500 font-semibold mt-1 flex items-center gap-1">
                          <i className="ri-arrow-right-line text-xs"></i>
                          Corrigir agora
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {totalCount > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
              <Link
                to="/admin/relatorios?tab=broken-links"
                onClick={() => setOpen(false)}
                className="text-xs text-orange-500 font-semibold hover:underline cursor-pointer flex items-center gap-1"
              >
                <i className="ri-links-line"></i>
                Abrir relatório de links quebrados
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
